<template>
    <div>
        <div ref="target" class="h-full">
            <slot></slot>
        </div>

        <transition name="fade">
            <div
                ref="tooltip"
                v-show="showTooltip"
                class="tooltip shadow py-8 px-12 text-body-sm font-semibold z-50 rounded"
                :class="[ display === 'dark' ? 'bg-primary text-neutral-white' : 'bg-neutral-white text-primary', tooltipWidth ?? 'w-fit' ]"
                @click="$emit('tooltip-click', $event)"
            >
                <slot name="tooltip">
                    <span class="first-letter:uppercase">{{ content }}</span>
                </slot>
                <div
                    v-if="!noArrow"
                    data-popper-arrow
                    class="arrow"
                    :class="display === 'dark' ? 'before:bg-primary' : 'before:bg-neutral-white'"
                ></div>
            </div>
        </transition>
    </div>
</template>

<script>
import { createPopper } from '@popperjs/core';

export default {
    props: {
        content: {
            type: String,
        },
        disabled: {
            type: Boolean,
        },
        position: {
            type: String,
            default: 'auto',
            validator: value => ['auto', 'right', 'left', 'top', 'bottom'].includes(value),
        },
        display: {
            type: String,
            default: 'dark',
            validator: value => ['dark', 'light'].includes(value),
        },
        delayed: Boolean,
        offset: {
            type: String,
            default: '0,10',
        },
        forceShow: {
            type: Boolean,
            default: undefined
        },
        noArrow: Boolean,
        customPosition: Boolean,
        forcedPosition: String,
        tooltipWidth: String,
    },
    data: function () {
        return {
            overTarget: false,
        }
    },
    computed: {
        showTooltip () {
            if (this.forceShow !== undefined) {
                this.updatePopper();
                return this.forceShow;
            }

            return this.overTarget && ! this.disabled;
        },
    },
    methods: {
        show () {
            this.overTarget = true;
        },
        hide () {
            this.overTarget = false;
        },
        handleMouseEnter () {

            clearTimeout(this._timer);

            this._timer = setTimeout(() => {
                this.overTarget = true;
                this.updatePopper();
            }, this.delayed ? 700 : 200);
        },
        handleMouseLeave () {
            clearTimeout(this._timer);
            this.overTarget = false;
        },
        createPopper () {
            this.$nextTick(async () => {

                this._popper = await createPopper(this._target, this._tooltip, {
                    placement: this.position,
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: this.offset.split(',').map(val => parseInt(val)),
                            },
                        },
                    ]
                });
                this.forcePosition();
            });
        },
        async updatePopper () {
            if (this._popper){
                await this._popper.update()
                this.forcePosition();
            } else {
                this.createPopper();
            }
        },
        forcePosition() {
            if (this.customPosition) {
                // get the width of the tooltip, and set the translate value to half of it
                const tooltipWidth = this._tooltip.offsetWidth;
                this._tooltip.style.transform = `translate(-${tooltipWidth/2}px, -20px)`;
                if (this.forcedPosition) {
                    this._tooltip.style.left = this.forcedPosition;
                }
            }
        }
    },
    watch: {
        forcedPosition() {
            this.updatePopper();
        },
        disabled() {
            this.updatePopper();
        },
    },
    mounted: function () {
        this._target = this.$refs.target;
        this._tooltip = this.$refs.tooltip;

        this._target.addEventListener('mouseenter', this.handleMouseEnter);
        this._target.addEventListener('mouseleave', this.handleMouseLeave);
        this.forcePosition();
    }
}
</script>

<style scoped>

.arrow {

    @apply absolute w-12 h-12;
    z-index: -1;

    &::before {
        @apply absolute w-12 h-12;
        z-index: -1;
        content: '';
        transform: rotate(45deg);
    }
}

.tooltip[data-popper-placement^='top'] > .arrow {
    bottom: -3px;
}

.tooltip[data-popper-placement^='bottom'] > .arrow {
    top: -3px;
}

.tooltip[data-popper-placement^='left'] > .arrow {
    right: -3px;
}

.tooltip[data-popper-placement^='right'] > .arrow {
    left: -3px;
}

.fade-enter-active, .fade-leave-active {
    transition: opacity .3s;
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
}
</style>
